import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import {
  Button,
  useToast,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  RadioGroup,
  RadioGroupItem,
} from '@org/ui';
import { Flag, AlertTriangle } from 'lucide-react';

type ReportTargetType = 'post' | 'comment' | 'message' | 'user';
type ReportReason = 'spam' | 'harassment' | 'inappropriate' | 'misinformation' | 'other';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetType: ReportTargetType;
  targetId: string;
  targetName?: string;
}

function useReportReasons() {
  const { t } = useTranslation('community');
  return [
    { value: 'spam' as ReportReason, label: t('reportModal.reasonSpam'), description: t('reportModal.reasonSpamDesc') },
    { value: 'harassment' as ReportReason, label: t('reportModal.reasonHarassment'), description: t('reportModal.reasonHarassmentDesc') },
    { value: 'inappropriate' as ReportReason, label: t('reportModal.reasonInappropriate'), description: t('reportModal.reasonInappropriateDesc') },
    { value: 'misinformation' as ReportReason, label: t('reportModal.reasonMisinformation'), description: t('reportModal.reasonMisinformationDesc') },
    { value: 'other' as ReportReason, label: t('reportModal.reasonOther'), description: t('reportModal.reasonOtherDesc') },
  ];
}

export function ReportModal({ isOpen, onClose, targetType, targetId, targetName }: ReportModalProps) {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const reportReasons = useReportReasons();

  const createReport = communityTrpc.moderation.createReport.useMutation({
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = () => {
    if (!selectedReason) return;

    createReport.mutate({
      targetType,
      targetId,
      reason: selectedReason,
      description: description.trim() || undefined,
    });
  };

  const handleClose = () => {
    setSelectedReason(null);
    setDescription('');
    setSubmitted(false);
    onClose();
  };

  const targetLabel = targetType;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-amber-500" />
            {t('reportModal.title', { target: targetLabel })}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Flag className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium">{t('reportModal.submitted')}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('reportModal.submittedMessage')}
            </p>
            <Button onClick={handleClose} className="mt-6">
              {t('reportModal.close')}
            </Button>
          </div>
        ) : (
          <div>
            {targetName && (
              <div className="mb-4 rounded-lg bg-muted p-3">
                <p className="text-sm text-muted-foreground">
                  {t('reportModal.reporting')}<span className="font-medium text-foreground">{targetName}</span>
                </p>
              </div>
            )}

            <div className="mb-4">
              <p className="mb-3 text-sm font-medium">{t('reportModal.whyReporting', { target: targetLabel })}</p>
              <RadioGroup
                value={selectedReason ?? ''}
                onValueChange={(value) => setSelectedReason(value as ReportReason)}
              >
                {reportReasons.map((reason) => (
                  <label
                    key={reason.value}
                    htmlFor={`report-reason-${reason.value}`}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                      selectedReason === reason.value
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <RadioGroupItem
                      id={`report-reason-${reason.value}`}
                      value={reason.value}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="font-medium">{reason.label}</p>
                      <p className="text-xs text-muted-foreground">{reason.description}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {selectedReason && (
              <div className="mb-4">
                <label className="mb-2 block text-sm font-medium">
                  {t('reportModal.additionalDetails')}
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('reportModal.detailsPlaceholder')}
                  rows={3}
                  maxLength={1000}
                />
              </div>
            )}

            <div className="flex items-center gap-3 border-t border-border pt-4">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              <p className="flex-1 text-xs text-muted-foreground">
                {t('reportModal.falseReportWarning')}
              </p>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!selectedReason || createReport.isPending}
                className="gap-2"
              >
                <Flag className="h-4 w-4" />
                {createReport.isPending ? t('reportModal.submitting') : t('reportModal.submit')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function useReportModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [target, setTarget] = useState<{
    type: ReportTargetType;
    id: string;
    name?: string;
  } | null>(null);

  const openReport = (type: ReportTargetType, id: string, name?: string) => {
    setTarget({ type, id, name });
    setIsOpen(true);
  };

  const closeReport = () => {
    setIsOpen(false);
    setTarget(null);
  };

  return {
    isOpen,
    target,
    openReport,
    closeReport,
    ReportModalComponent: target ? (
      <ReportModal
        isOpen={isOpen}
        onClose={closeReport}
        targetType={target.type}
        targetId={target.id}
        targetName={target.name}
      />
    ) : null,
  };
}
